/**
 * Hook to fetch paginated facet options for a given facet.
 * Supports both collection-based and enum-based value sources.
 * Accumulates results across pages for infinite scroll behavior.
 */

import { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { useDebounce } from 'use-debounce';

import type { FacetOption, PaginatedOptions } from './types';
import { useTypesenseAPIKey } from './useTypesenseAPIKey';
import type { TypesenseHighlight } from '@/api/typesense/types';
import { typesense } from '@/api/typesense/typesenseClient';
import { formatEnumLabel } from '@/lib/resource-config';
import type { ResourceConfig } from '@/lib/resource-config';
import { highlightText } from '@/lib/utils/text';

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 200;

interface UseFacetOptionsParams {
  config: ResourceConfig;
  facetKey: string;
  searchTerm: string;
  page: number;
}

/**
 * Fetches paginated facet options for autocomplete.
 * - For collection-based sources: queries Typesense options collection
 * - For enum sources: filters in-memory with pagination
 *
 * @param params - Hook parameters
 * @returns React Query result with paginated options
 */
export function useFacetOptions({
  config,
  facetKey,
  searchTerm,
  page,
}: UseFacetOptionsParams) {
  const facetConfig = config.facets[facetKey];

  // Get API key for the options collection
  // Use apiKeyName override if specified, otherwise use collection name
  const collectionName =
    facetConfig?.valueSource.kind === 'collection'
      ? facetConfig.valueSource.apiKeyName || facetConfig.valueSource.collection
      : '';

  const { apiKey } = useTypesenseAPIKey(collectionName);

  // Debounce search term by 200ms
  const [debouncedTerm] = useDebounce(searchTerm, DEBOUNCE_MS);

  // Accumulate options across pages for infinite scroll
  const [allOptions, setAllOptions] = useState<FacetOption[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);

  // Build query key with stable primitives only (no object references)
  // This ensures cache stability across renders even if facetConfig object identity changes
  const queryKey = [
    'facetOptions',
    config.resourceKey,
    facetKey,
    facetConfig?.field,
    facetConfig?.valueSource.kind,
    facetConfig?.valueSource.kind === 'collection'
      ? facetConfig.valueSource.apiKeyName || facetConfig.valueSource.collection
      : undefined,
    debouncedTerm,
    page,
  ];

  const queryResult = useQuery<PaginatedOptions>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- facetConfig object excluded to maintain cache stability; primitives from facetConfig are included in queryKey
    queryKey,
    queryFn: async () => {
      if (!facetConfig) {
        return { options: [], hasMore: false, total: 0 };
      }

      const { valueSource } = facetConfig;

      if (valueSource.kind === 'enum') {
        // In-memory filtering for enum values
        return fetchEnumOptions(
          valueSource.values,
          debouncedTerm,
          page,
          config.resourceKey,
        );
      } else {
        // Query Typesense options collection
        if (!apiKey) {
          console.error('[useFacetOptions] API key not available');
          return { options: [], hasMore: false, total: 0 };
        }
        return fetchCollectionOptions(
          apiKey,
          valueSource,
          debouncedTerm,
          page,
          config.resourceKey,
        );
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled:
      !!facetConfig && (facetConfig.valueSource.kind === 'enum' || !!apiKey),
  });

  // Reset accumulated options when search term or facet changes
  useEffect(() => {
    setAllOptions([]);
    setCurrentPage(0);
    setHasMore(false);
    setTotal(0);
  }, [debouncedTerm, facetKey]);

  // Accumulate new options when page changes
  useEffect(() => {
    if (queryResult.data?.options && queryResult.isSuccess) {
      if (page === 0) {
        // Reset on first page
        setAllOptions(queryResult.data.options);
      } else if (page > currentPage) {
        // Append new page results, removing duplicates by slug
        setAllOptions((prev) => {
          const existingSlugs = new Set(prev.map((opt) => opt.slug));
          const newOptions = queryResult.data.options.filter(
            (opt) => !existingSlugs.has(opt.slug),
          );
          return [...prev, ...newOptions];
        });
      }
      setCurrentPage(page);
      setHasMore(queryResult.data.hasMore);
      setTotal(queryResult.data.total);
    }
  }, [queryResult.data, queryResult.isSuccess, page, currentPage]);

  // Return accumulated results with stable references during fetches
  return {
    ...queryResult,
    data: {
      options: allOptions,
      hasMore: hasMore,
      total: total,
    },
  };
}

/**
 * Fetches options from an enum value source (in-memory pagination)
 */
function fetchEnumOptions(
  values: string[],
  searchTerm: string,
  page: number,
  resourceKey: string,
): PaginatedOptions {
  // Filter by search term (case-insensitive) against BOTH slug and formatted label
  const filtered = searchTerm
    ? values.filter((v) => {
        const label = formatEnumLabel(v, resourceKey);
        const term = searchTerm.toLowerCase();
        return (
          v.toLowerCase().includes(term) || label.toLowerCase().includes(term)
        );
      })
    : values;

  // Paginate
  const startIndex = page * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const paginated = filtered.slice(startIndex, endIndex);

  return {
    options: paginated.map((slug) => {
      const label = formatEnumLabel(slug, resourceKey);

      let highlightedLabel: string | undefined;
      if (searchTerm) {
        highlightedLabel = highlightText(label, searchTerm);
      }

      return {
        id: slug, // For enums, id === slug
        slug,
        label,
        highlightedLabel,
      };
    }),
    hasMore: endIndex < filtered.length,
    total: filtered.length,
  };
}

/**
 * Fetches options from a Typesense collection
 */
async function fetchCollectionOptions(
  apiKey: string,
  valueSource: Extract<
    ResourceConfig['facets'][string]['valueSource'],
    { kind: 'collection' }
  >,
  searchTerm: string,
  page: number,
  resourceKey: string,
): Promise<PaginatedOptions> {
  const {
    collection,
    slugField,
    labelField,
    sortField,
    translateLabels,
    translationNamespace,
  } = valueSource;

  try {
    // Only search by labelField since slugField might be numeric (e.g., SourceId)
    // Typesense query_by only accepts string/string[] fields
    const searchRequest = {
      searches: [
        {
          collection,
          'x-typesense-api-key': apiKey,
          q: searchTerm || '*',
          query_by: labelField, // Only search by label (Name), not slug (might be ID)
          per_page: PAGE_SIZE,
          page: page + 1, // Typesense pages are 1-indexed
          sort_by: sortField ? `${sortField}:asc` : `${labelField}:asc`,
        },
      ],
    };

    const multiSearchResults =
      await typesense.multiSearch.perform(searchRequest);
    const result = multiSearchResults.results[0] as {
      hits?: Array<{
        document: Record<string, unknown>;
        highlight?: TypesenseHighlight;
      }>;
      found?: number;
    };

    const options: FacetOption[] = (result.hits || []).map((hit) => {
      const id = String(hit.document[valueSource.idField]);
      const rawLabel = String(hit.document[labelField] || '').trim();

      // Handle missing labels (data quality issue where Name field is empty/whitespace)
      let label = rawLabel || `Unknown (ID: ${id})`;

      if (!rawLabel) {
        console.warn(
          `[useFacetOptions] Missing label field for ${collection} record:`,
          {
            collection,
            id,
            labelField,
            missingLabel: true,
          },
        );
      }

      // Apply translation if translateLabels flag is set
      if (translateLabels && rawLabel) {
        // Use translationNamespace if specified, otherwise use resourceKey
        const namespace = translationNamespace || resourceKey;
        label = formatEnumLabel(rawLabel, namespace);
      }

      // Extract highlight snippet (server-side for non-translated, client-side for translated)
      let highlightedLabel: string | undefined;

      if (!translateLabels && hit.highlight?.[labelField]?.snippet) {
        // Server-side Typesense highlights for non-translated fields
        const snippet = hit.highlight[labelField].snippet;
        highlightedLabel = DOMPurify.sanitize(snippet, {
          ALLOWED_TAGS: ['mark'],
          ALLOWED_ATTR: [],
        });
      } else if (translateLabels && rawLabel && searchTerm) {
        highlightedLabel = highlightText(label, searchTerm);
      }

      const option: FacetOption = {
        id,
        slug: String(hit.document[slugField]),
        label,
        ...(highlightedLabel && { highlightedLabel }),
        // Store untranslated label for matching with facet counts
        ...(translateLabels && rawLabel && { untranslatedLabel: rawLabel }),
      };

      return option;
    });

    return {
      options,
      hasMore: (result.found || 0) > (page + 1) * PAGE_SIZE,
      total: result.found || 0,
    };
  } catch (error) {
    console.error(
      `[useFacetOptions] Error fetching options for ${collection}:`,
      error,
    );
    return { options: [], hasMore: false, total: 0 };
  }
}
